import type React from "react";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
	FirstDataRenderedEvent,
} from "ag-grid-community";
import { Grid, TextField } from "@material-ui/core";
import { ErrorModal } from "@dexteel/mesf-core";
import LazyLoading from "@/components/loaders/LazyLoading/LazyLoading";
import type { AssetPart } from "@/pages/asset-details/models/AssetPart";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

interface Props {
	allParts: AssetPart[];
	assetParts: AssetPart[];
	onSelectionChanged: (selectedParts: AssetPart[]) => void;
	isLoading: boolean;
}

export const PartsTable: React.FC<Props> = ({
	allParts,
	assetParts,
	onSelectionChanged,
	isLoading,
}) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const gridApiRef = useRef<GridApi | null>(null);

	const handlerFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFilterInput(e.target.value);
	};

	const selectedPartsSet = useMemo(() => {
		return new Set(assetParts.map((part) => part.Name));
	}, [assetParts]);

	const sortRows = useCallback(() => {
		if (gridApiRef.current) {
			gridApiRef.current.applyColumnState({
				state: [{ colId: "Name", sort: "asc", sortIndex: 0 }],
				defaultState: { sort: null },
			});
		}
	}, []);

	const loadingOverlayComponent = useMemo(() => LazyLoading, []);

	const rows = useMemo(() => {
		return allParts.map((part: AssetPart) => ({
			...part,
			isSelected: selectedPartsSet.has(part.Name),
		}));
	}, [allParts, selectedPartsSet]);

	const columns: ColDef[] = useMemo(
		() => [
			{
				headerName: "",
				flex: 1,
				maxWidth: 33,
				headerCheckboxSelection: true,
				checkboxSelection: true,
				sortIndex: 1,
			},
			{
				field: "Name",
				headerName: "Part Name",
				minWidth: 120,
				maxWidth: 120,
				flex: 2,
				sortable: true,
				sortIndex: 1,
				comparator: (valueA, valueB, nodeA, nodeB) => {
					if (nodeA.isSelected() && !nodeB.isSelected()) {
						return -1;
					} else if (!nodeA.isSelected() && nodeB.isSelected()) {
						return 1;
					} else {
						return (valueA || "").localeCompare(valueB || "");
					}
				},
			},
			{
				field: "Description",
				headerName: "Description",
				flex: 4,
				sortable: true,
				sortIndex: 1,
				comparator: (valueA, valueB, nodeA, nodeB) => {
					if (nodeA.isSelected() && !nodeB.isSelected()) {
						return -1;
					} else if (!nodeA.isSelected() && nodeB.isSelected()) {
						return 1;
					} else {
						return (valueA || "").localeCompare(valueB || "");
					}
				},
			},
			{
				field: "BinName",
				headerName: "Bin",
				minWidth: 100,
				maxWidth: 100,
				flex: 2,
				sortable: true,
				sortIndex: 1,
				comparator: (valueA, valueB, nodeA, nodeB) => {
					if (nodeA.isSelected() && !nodeB.isSelected()) {
						return -1;
					} else if (!nodeA.isSelected() && nodeB.isSelected()) {
						return 1;
					} else {
						return (valueA || "").localeCompare(valueB || "");
					}
				},
			},
			{
				field: "Qty",
				headerName: "Quantity",
				minWidth: 80,
				maxWidth: 80,
				flex: 1,
				sortable: true,
				sortIndex: 1,
				type: "numericColumn",
				comparator: (valueA, valueB, nodeA, nodeB) => {
					if (nodeA.isSelected() && !nodeB.isSelected()) {
						return -1;
					} else if (!nodeA.isSelected() && nodeB.isSelected()) {
						return 1;
					} else {
						return (valueA || 0) - (valueB || 0);
					}
				},
			},
		],
		[],
	);

	const onGridReady = useCallback((event: GridReadyEvent) => {
		gridApiRef.current = event.api;
	}, []);

	const selectInitialRows = useCallback(() => {
		if (gridApiRef.current && selectedPartsSet.size > 0) {
			gridApiRef.current.forEachNode((node) => {
				if (selectedPartsSet.has(node.data.Name)) {
					node.setSelected(true);
				}
			});
		}
	}, [selectedPartsSet]);

	const onFirstDataRendered = useCallback(
		(params: FirstDataRenderedEvent) => {
			if (assetParts && assetParts.length > 0) {
				setTimeout(() => {
					selectInitialRows();
					sortRows();
				}, 50);
			} else {
				sortRows();
			}
		},
		[assetParts, selectInitialRows, sortRows],
	);

	const onAgGridSelectionChanged = useCallback(
		(event: SelectionChangedEvent) => {
			const selectedRows = event.api.getSelectedRows();
			onSelectionChanged(selectedRows);
		},
		[onSelectionChanged],
	);

	useEffect(() => {
		if (gridApiRef.current) {
			gridApiRef.current.setGridOption("quickFilterText", filterInput.trim());
		}
	}, [filterInput]);

	useEffect(() => {
		if (gridApiRef.current && allParts.length > 0 && !isLoading) {
			setTimeout(() => {
				if (gridApiRef.current) {
					gridApiRef.current.deselectAll();
					selectInitialRows();
					sortRows();
					gridApiRef.current.refreshClientSideRowModel("sort");
				}
			}, 100);
		}
	}, [
		assetParts.length,
		allParts.length,
		isLoading,
		selectInitialRows,
		sortRows,
	]);

	return (
		<>
			<Grid
				container
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid item xs={12}>
					<TextField
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						fullWidth
					/>
				</Grid>
			</Grid>
			<div style={{ width: "100%", height: "60vh" }}>
				<Grid style={{ width: "100%", height: "60vh" }}>
					<AgGridReact
						getRowId={(params) => params.data.Name}
						rowHeight={30}
						headerHeight={25}
						animateRows={false}
						suppressRowVirtualisation={false}
						suppressColumnVirtualisation={false}
						rowBuffer={10}
						debounceVerticalScrollbar={true}
						onGridReady={onGridReady}
						rowData={rows}
						columnDefs={columns}
						defaultColDef={globalDefaultColDef}
						onFirstDataRendered={onFirstDataRendered}
						loadingOverlayComponent={loadingOverlayComponent}
						rowSelection="multiple"
						rowMultiSelectWithClick={true}
						onSelectionChanged={onAgGridSelectionChanged}
						suppressContextMenu={true}
						loading={isLoading}
					/>
				</Grid>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
